import { useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useReactFlow,
  MarkerType,
  type NodeMouseHandler,
  type DefaultEdgeOptions,
  type OnNodeDrag,
} from '@xyflow/react';
import { StudioNode } from './StudioNode';
import { LoopGroupNode } from './LoopGroupNode';
import { LoopEdge } from './LoopEdge';
import { AnimatedEdge } from './AnimatedEdge';
import { useFlowStore } from '../../store/flowStore';
import { nodeColors } from '../../theme/colors';
import type { NodeKind } from '../../types';

const nodeTypes = { studio: StudioNode, loopGroup: LoopGroupNode };
const edgeTypes = { animated: AnimatedEdge, loop: LoopEdge };

const defaultEdgeOptions: DefaultEdgeOptions = {
  type: 'animated',
  markerEnd: { type: MarkerType.ArrowClosed, color: '#C2CCD8' },
  style: { stroke: '#C2CCD8', strokeWidth: 2 },
};

export function FlowCanvas() {
  const wrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition, getIntersectingNodes } = useReactFlow();

  const nodes = useFlowStore((s) => s.nodes);
  const edges = useFlowStore((s) => s.edges);
  const onNodesChange = useFlowStore((s) => s.onNodesChange);
  const onEdgesChange = useFlowStore((s) => s.onEdgesChange);
  const onConnect = useFlowStore((s) => s.onConnect);
  const addNode = useFlowStore((s) => s.addNode);
  const reparentNode = useFlowStore((s) => s.reparentNode);
  const requestDeleteNodes = useFlowStore((s) => s.requestDeleteNodes);
  const setSelected = useFlowStore((s) => s.setSelected);

  // Container de loop cujo retângulo contém o ponto (em coords do canvas).
  function loopContainerAt(p: { x: number; y: number }): string | null {
    const containers = useFlowStore.getState().nodes.filter((n) => n.type === 'loopGroup');
    for (const c of containers) {
      const w = (c.width as number) ?? Number((c.style as { width?: number })?.width) ?? 0;
      const h = (c.height as number) ?? Number((c.style as { height?: number })?.height) ?? 0;
      if (p.x >= c.position.x && p.x <= c.position.x + w && p.y >= c.position.y && p.y <= c.position.y + h) {
        return c.id;
      }
    }
    return null;
  }

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const kind = e.dataTransfer.getData('application/studio-node') as NodeKind;
      if (!kind) return;
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      // Solto sobre um container de loop (e não sendo outro loop) → entra como filho.
      const parentId = kind !== 'loop' ? loopContainerAt(position) : null;
      addNode(kind, position, parentId ?? undefined);
    },
    [screenToFlowPosition, addNode],
  );

  const onNodeClick: NodeMouseHandler = useCallback((_e, node) => setSelected(node.id), [setSelected]);

  // Arrastar um nó para dentro/fora de um container de loop → re-parenta.
  const onNodeDragStop = useCallback<OnNodeDrag>(
    (_e, node) => {
      if (node.type === 'loopGroup') return;
      const overlap = getIntersectingNodes(node).find((n) => n.type === 'loopGroup');
      reparentNode(node.id, overlap ? overlap.id : null);
    },
    [getIntersectingNodes, reparentNode],
  );

  return (
    <div ref={wrapper} className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onNodeDragStop={onNodeDragStop}
        onBeforeDelete={async ({ nodes: toDelete }) => {
          // Tecla Delete sobre nó(s) → abre confirmação e bloqueia a exclusão automática.
          if (toDelete.length) {
            requestDeleteNodes(toDelete.map((n) => n.id));
            return false;
          }
          return true; // só arestas → segue normalmente
        }}
        onPaneClick={() => setSelected(null)}
        onDrop={onDrop}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        snapToGrid
        snapGrid={[16, 16]}
        fitView
        proOptions={{ hideAttribution: true }}
        deleteKeyCode={['Delete', 'Backspace']}
        className="bg-cloud"
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1.5} color="#CBD5E1" />
        <Controls className="!shadow-panel" />
        <MiniMap
          pannable
          zoomable
          nodeColor={(n) => nodeColors[(n.data as { kind: NodeKind }).kind] ?? '#94A3B8'}
          maskColor="rgba(8,24,43,0.06)"
        />
      </ReactFlow>
    </div>
  );
}
